import { Card, CardContent, Typography, Box } from '@mui/material';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import type { HistoricalDataPoint } from '../utils/dealCalculations';
import type { ListenerProjection } from '../utils/dealCalculations';

interface TrajectoryChartProps {
    history: HistoricalDataPoint[];
    projections: ListenerProjection[];
    currentListeners: number;
}

const fmtNum = (val: number): string => {
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
    return String(val);
};

export const TrajectoryChart = ({
    history,
    projections,
    currentListeners,
}: TrajectoryChartProps) => {
    // Build unified chart data: historical + projected
    const chartData: any[] = [];

    // Historical points — collapse to one per month (last snapshot wins)
    const sorted = [...history].sort(
        (a, b) => a.date.getTime() - b.date.getTime()
    );
    const monthlyHistory = new Map<string, { date: Date; monthlyListeners: number }>();
    for (const point of sorted) {
        const monthKey = format(point.date, 'yyyy-MM');
        monthlyHistory.set(monthKey, point); // last snapshot in each month wins
    }
    for (const point of monthlyHistory.values()) {
        chartData.push({
            label: format(point.date, 'MMM yyyy'),
            historical: point.monthlyListeners,
            type: 'historical',
        });
    }

    // "Today" divider label
    const todayLabel = format(new Date(), 'MMM yyyy');

    // Projected points (show months 3, 6, 12, 18, 24, 30, 36)
    const milestones = [3, 6, 12, 18, 24, 30, 36];
    for (const m of milestones) {
        const proj = projections.find((p) => p.month === m);
        if (!proj) continue;

        const futureDate = new Date();
        futureDate.setMonth(futureDate.getMonth() + m);

        chartData.push({
            label: format(futureDate, 'MMM yyyy'),
            conservative: proj.conservative,
            moderate: proj.moderate,
            optimistic: proj.optimistic,
            type: 'projected',
        });
    }

    if (chartData.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6">Listener Trajectory</Typography>
                    <Box display="flex" justifyContent="center" py={4}>
                        <Typography variant="body2" color="textSecondary">
                            No historical data available
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        return (
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <Typography variant="body2" fontWeight="bold">{label}</Typography>
                {payload.map((entry: any, i: number) => (
                    <Typography key={i} variant="caption" display="block" sx={{ color: entry.color }}>
                        {entry.name}: {fmtNum(entry.value)}
                    </Typography>
                ))}
            </Box>
        );
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Listener Trajectory
                </Typography>
                <Box height={350}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" fontSize={11} />
                            <YAxis tickFormatter={fmtNum} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <ReferenceLine x={todayLabel} stroke="#666" strokeDasharray="3 3" label="Now" />

                            {/* Historical line */}
                            <Line
                                type="monotone"
                                dataKey="historical"
                                name="Historical"
                                stroke="#1DB954"
                                strokeWidth={3}
                                dot={{ r: 4 }}
                                connectNulls
                            />

                            {/* Projected lines */}
                            <Line
                                type="monotone"
                                dataKey="conservative"
                                name="Conservative"
                                stroke="#ff9800"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ r: 3 }}
                                connectNulls
                            />
                            <Line
                                type="monotone"
                                dataKey="moderate"
                                name="Moderate"
                                stroke="#2196f3"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls
                            />
                            <Line
                                type="monotone"
                                dataKey="optimistic"
                                name="Optimistic"
                                stroke="#4caf50"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ r: 3 }}
                                connectNulls
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};
